/**
 * Error handlers for login
 * Extracted from useLoginController.ts to reduce file size
 */

import { ValidationError } from "class-validator";
import { useRouter } from "next/router";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { Routes, buildRoute } from "@Front/Config/Routes";
import { PUBLIC_AUTH_EMAIL_ACTOR_CHOICE_MESSAGE } from "@Front/constants/publicAuthUserMessages";
import { coalesceThrownApiErrorText } from "@Front/Utils/userFacingMessageFromApiClientError";

type HttpErrorPayload = {
	http_status?: number;
	message?: string;
};

/**
 * Extract HTTP error from unknown error
 */
export const extractHttpError = (error: unknown): HttpErrorPayload => {
	const payload = error as HttpErrorPayload | undefined;
	return {
		http_status: typeof payload?.http_status === "number" ? payload.http_status : undefined,
		message: typeof payload?.message === "string" ? payload.message : undefined,
	};
};

/**
 * Redirect after successful login
 */
export function redirectAfterLogin(router: ReturnType<typeof useRouter>): void {
	const folderUid = router.query["folderUid"];
	if (typeof folderUid === "string" && folderUid.length > 0) {
		const dashboardPath = buildRoute(Routes.pages.ClientDashboard.base, { folderUid });
		router.push(dashboardPath);
	} else {
		router.push(Routes.pages.Folder.Select);
	}
}

function tryRedirectThirdPartyLoginFromEmailError(
	message: string | undefined,
	values: { [key: string]: string },
	router: ReturnType<typeof useRouter>,
): boolean {
	if (!message || typeof message !== "string" || !message.startsWith("THIRD_PARTY_EMAIL:")) {
		return false;
	}
	const emailValue = values["email"];
	if (!emailValue) {
		return true;
	}
	router.push(`/third-party/login?email=${encodeURIComponent(emailValue)}&autoSend=true`);
	return true;
}

function setActorChoiceRequiredEmailError(
	http_status: number | undefined,
	setValidationErrors: (errors: ValidationError[]) => void,
): void {
	setValidationErrors([
		{
			property: "email",
			constraints: {
				[String(http_status ?? "403")]: PUBLIC_AUTH_EMAIL_ACTOR_CHOICE_MESSAGE,
			},
		},
	]);
}

/**
 * Handle email submission error
 */
export function handleEmailSubmitError(
	error: unknown,
	values: { [key: string]: string },
	router: ReturnType<typeof useRouter>,
	setValidationErrors: (errors: ValidationError[]) => void,
): void {
	const { http_status, message } = extractHttpError(error);
	const errorMessage = coalesceThrownApiErrorText(error);

	if (tryRedirectThirdPartyLoginFromEmailError(message, values, router)) {
		return;
	}

	if (message && typeof message === "string" && message.startsWith("ACTOR_CHOICE_REQUIRED:")) {
		setActorChoiceRequiredEmailError(http_status, setValidationErrors);
		return;
	}

	LoggerService.error("mailVerifySms failed", {
		error: errorMessage,
		http_status,
		message,
		email: values["email"],
		fullError: error,
	});
	setValidationErrors([
		{
			property: "email",
			constraints: {
				[String(http_status ?? "unknown")]: message ?? "Une erreur est survenue.",
			},
		},
	]);
}

/**
 * Handle TOTP verification error
 */
export function handleTotpVerificationError(
	error: unknown,
	email: string | undefined,
	totpCodeValue: string | undefined,
	setValidationErrors: (errors: ValidationError[]) => void,
): void {
	const { http_status, message } = extractHttpError(error);
	LoggerService.error("verifyTotpCode failed", { error, email, totpCodeValue });
	setValidationErrors([
		{
			property: "totpCode",
			constraints: {
				[String(http_status ?? "unknown")]: message ?? "Une erreur est survenue.",
			},
		},
	]);
}

/**
 * Handle password error
 */
export function handlePasswordError(
	error: unknown,
	email: string,
	property: "password" | "totpCode",
	setValidationErrors: (errors: ValidationError[]) => void,
): void {
	const { http_status, message } = extractHttpError(error);
	LoggerService.error(property === "password" ? "login failed" : "setPassword failed", { error, email });
	setValidationErrors([
		{
			property,
			constraints: {
				[String(http_status ?? "unknown")]: message ?? "Une erreur est survenue.",
			},
		},
	]);
}

